// Newsletter image row block.
//
// Top-level container holding 1–2 image-row-item children. The hard cap is
// `MAX_IMAGE_ROW_ITEMS`. Exported so callers (toolbar/file picker) can clamp
// selection explicitly instead of silently dropping extras.
//
// Schema:
//   newsletterImageRow      content: "newsletterImageRowItem{1,MAX_IMAGE_ROW_ITEMS}"
//   newsletterImageRowItem  content: "newsletterImageMedia imageCaption"
//
// NodeView:
//   <div data-newsletter-image-row data-gap class="nl-image-row">
//     <div class="nl-image-row__items">  ← contentDOM (PM renders <img> children here)
//     <button class="nl-image-row__add">+ image  ← chrome, contentEditable=false
//
// Adding an image requires a file picker, which lives on the Stimulus
// controller. The NodeView calls a storage callback that the controller
// registers on connect.

import { Node, mergeAttributes } from "@tiptap/core"
import { NodeSelection } from "@tiptap/pm/state"

import { idAttributeSpec } from "./attributes"
import { buildResizeHandle } from "./image"
import { deleteBlockAt, runStructuralCommand } from "./commands"

const VALID_GAP = ["tight", "normal", "loose"]
export const MAX_IMAGE_ROW_ITEMS = 2
const ITEM_WIDTH_MIN = 15
const ITEM_WIDTH_MAX = 85

function normalizeItemWidth(raw, fallback = 50) {
  if (raw === null || raw === undefined || raw === "") return fallback
  const n = typeof raw === "number" ? raw : parseFloat(String(raw))
  if (!Number.isFinite(n)) return fallback
  return Math.max(ITEM_WIDTH_MIN, Math.min(ITEM_WIDTH_MAX, Math.round(n)))
}

function defaultWidthFor(count) {
  return Math.round(100 / Math.max(1, count))
}

// Per-cell wrapper holding one image + a caption slot. Required caption keeps
// the schema simple: PM always renders a <figcaption> alongside the <img>;
// empty captions display a Placeholder-driven "Write caption…" prompt.
//
// NodeView adds a ✕ button shown only when the inner image is selected. It's
// the only way to remove a single image from a row (keyboard delete is
// blocked by StructuralDeleteGuard + image media's own handler).
export const NewsletterImageRowItem = Node.create({
  name: "newsletterImageRowItem",
  content: "newsletterImageMedia imageCaption",
  selectable: false,
  draggable: false,

  addAttributes() {
    return {
      widthPercent: {
        default: 50,
        parseHTML: (el) => {
          const raw = el.getAttribute("data-width-percent")
          if (raw !== null && raw !== "") return normalizeItemWidth(raw)
          // Legacy: parse old flex-weight attr if it's all that exists.
          const legacyFlex = el.getAttribute("data-flex")
          if (legacyFlex) {
            const f = parseInt(legacyFlex, 10)
            if (Number.isFinite(f) && f > 0) {
              // Without sibling context here we just default to 50%; row-level
              // normalization handles the rest on next edit.
              return 50
            }
          }
          return 50
        },
        renderHTML: (attrs) => ({
          "data-width-percent": String(normalizeItemWidth(attrs.widthPercent)),
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "div[data-newsletter-image-row-item]" }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-newsletter-image-row-item": "" }),
      0,
    ]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => buildRowItemView(node, editor, getPos)
  },

  addCommands() {
    return {
      // Update one item's width and rebalance the rest so the row totals
      // ~100%. Siblings shrink/grow proportionally to their previous shares.
      setImageRowItemWidth:
        (itemPos, nextPercent) =>
        ({ state, dispatch, tr }) => {
          const item = state.doc.nodeAt(itemPos)
          if (!item || item.type.name !== "newsletterImageRowItem") return false
          const $item = state.doc.resolve(itemPos)
          const row = $item.parent
          if (!row || row.type.name !== "newsletterImageRow") return false

          const rowStart = $item.before($item.depth) + 1
          const clamped = normalizeItemWidth(nextPercent)

          const siblings = []
          let sumOthers = 0
          row.forEach((child, offset) => {
            if (child === item) return
            const w = normalizeItemWidth(child.attrs.widthPercent)
            siblings.push({ pos: rowStart + offset, w })
            sumOthers += w
          })

          if (!dispatch) return true

          tr.setNodeMarkup(itemPos, undefined, { ...item.attrs, widthPercent: clamped })

          const remaining = 100 - clamped
          if (siblings.length > 0) {
            // Distribute `remaining` across siblings in proportion to their
            // previous widths. If they collectively had no width, split evenly.
            siblings.forEach(({ pos, w }, idx) => {
              let nextW
              if (sumOthers > 0) {
                nextW = (w / sumOthers) * remaining
              } else {
                nextW = remaining / siblings.length
              }
              nextW = normalizeItemWidth(Math.round(nextW))
              // Pull the latest attrs from the in-progress transaction's doc,
              // so we preserve any other attrs we don't touch.
              const node = tr.doc.nodeAt(pos)
              if (node) {
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, widthPercent: nextW })
              }
            })
          }

          dispatch(tr)
          return true
        },
    }
  },
})

function buildRowItemView(initialNode, editor, getPos) {
  const dom = document.createElement("div")
  dom.setAttribute("data-newsletter-image-row-item", "")
  dom.className = "nl-image-row-item"

  const contentDOM = document.createElement("div")
  contentDOM.className = "nl-image-row-item__content"

  const resizeHandle = buildResizeHandle({
    direction: "right",
    onResize: ({ delta, startWidth }) => {
      const pos = typeof getPos === "function" ? getPos() : null
      if (pos === null || pos === undefined) return
      const rowEl = dom.parentElement
      const rowWidth = rowEl?.clientWidth || 600
      const deltaPct = (delta / rowWidth) * 100
      const target = startWidth + deltaPct
      editor.chain().focus().setImageRowItemWidth(pos, target).run()
    },
    getStartWidth: () => {
      const pos = typeof getPos === "function" ? getPos() : null
      if (pos === null || pos === undefined) return 50
      return normalizeItemWidth(editor.state.doc.nodeAt(pos)?.attrs.widthPercent)
    },
  })
  resizeHandle.classList.add("nl-image-row-item__resize-handle")

  const deleteBtn = document.createElement("button")
  deleteBtn.type = "button"
  deleteBtn.className = "nl-image-row-item__delete"
  deleteBtn.contentEditable = "false"
  deleteBtn.setAttribute("aria-label", "Delete image")
  deleteBtn.setAttribute("title", "Delete image")
  deleteBtn.innerHTML = `
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/>
    </svg>
  `
  deleteBtn.addEventListener("mousedown", (event) => event.preventDefault())
  deleteBtn.addEventListener("click", (event) => {
    event.preventDefault()
    const pos = typeof getPos === "function" ? getPos() : null
    if (pos === null || pos === undefined) return
    const itemNode = editor.state.doc.nodeAt(pos)
    if (!itemNode) return

    const $item = editor.state.doc.resolve(pos)
    const row = $item.parent
    const rowPos = $item.before($item.depth)
    const indexInRow = $item.index($item.depth - 1)

    // If this is the last image in the row, drop the row entirely via the
    // shared block-delete helper (preserves the doc-never-empty invariant).
    // Otherwise use the row-aware item remover which also rebalances widths.
    if (row && row.type.name === "newsletterImageRow" && row.childCount <= 1) {
      runStructuralCommand(editor, deleteBlockAt(rowPos))
    } else {
      editor.chain().focus().removeRowImageAt(rowPos, indexInRow).run()
    }
  })

  dom.append(contentDOM, deleteBtn, resizeHandle)

  const refresh = (node) => {
    const widthPercent = normalizeItemWidth(node.attrs.widthPercent)
    dom.dataset.widthPercent = String(widthPercent)
    dom.style.setProperty("--nl-item-w", `${widthPercent}%`)
  }
  refresh(initialNode)

  return {
    dom,
    contentDOM,
    update(node) {
      if (node.type.name !== initialNode.type.name) return false
      refresh(node)
      return true
    },
    ignoreMutation(mutation) {
      return !contentDOM.contains(mutation.target)
    },
  }
}

export const NewsletterImageRow = Node.create({
  name: "newsletterImageRow",
  group: "newsletterBlock",
  content: `newsletterImageRowItem{1,${MAX_IMAGE_ROW_ITEMS}}`,
  draggable: true,
  selectable: true,
  isolating: true,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addStorage() {
    return {
      // Controller registers this callback on connect. Called when the user
      // clicks "+ image" inside a row. Receives the row's position. The
      // controller is expected to pop a file picker and dispatch an
      // addRowImage command once a file is chosen.
      onAddImageClick: null,
    }
  },

  addAttributes() {
    return {
      id: idAttributeSpec,
      gap: {
        default: "normal",
        parseHTML: (el) => {
          const value = el.getAttribute("data-gap")
          return VALID_GAP.includes(value) ? value : "normal"
        },
        renderHTML: (attrs) => ({ "data-gap": attrs.gap }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "div[data-newsletter-image-row]" }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-newsletter-image-row": "",
      }),
      0,
    ]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => buildRowView(node, editor, getPos)
  },

  addCommands() {
    return {
      insertNewsletterImageRow:
        ({ srcs = [] } = {}) =>
        ({ state, dispatch, tr, view }) => {
          const slice = srcs.slice(0, MAX_IMAGE_ROW_ITEMS)
          if (slice.length === 0) return false
          // Honor caller-supplied widthPercent (typically derived from
          // image aspect ratios so all images render at the same height).
          // Fall back to an even split when no widths are provided.
          const evenWidth = defaultWidthFor(slice.length)
          const items = slice
            .map((entry) => {
              const widthPercent = entry && typeof entry === "object" && entry.widthPercent
                ? entry.widthPercent
                : evenWidth
              return buildItem(state.schema, entry, widthPercent)
            })
            .filter(Boolean)
          if (items.length === 0) return false

          const row = state.schema.nodes.newsletterImageRow.createAndFill({}, items)
          if (!row) return false

          const $from = state.selection.$from
          const insertPos = $from.depth === 0 ? $from.pos : $from.after(1)

          if (!dispatch) return true

          tr.insert(insertPos, row)
          tr.setSelection(NodeSelection.create(tr.doc, insertPos))
          dispatch(tr.scrollIntoView())
          view?.focus()
          return true
        },

      // `width`/`height` are accepted for API symmetry with row-insert (so
      // call sites pass natural dimensions unconditionally) but aren't yet
      // applied here — adding a single image to an existing row keeps the
      // simple even-split behavior since we don't track natural ratios on
      // the siblings.
      addRowImage:
        (rowPos, { src = "", alt = "" } = {}) =>
        ({ state, dispatch, tr }) => {
          const row = state.doc.nodeAt(rowPos)
          if (!row || row.type.name !== "newsletterImageRow") return false
          if (row.childCount >= MAX_IMAGE_ROW_ITEMS) return false

          const newCount = row.childCount + 1
          const evenWidth = defaultWidthFor(newCount)

          const item = buildItem(state.schema, { src, alt }, evenWidth)
          if (!item) return false

          if (!dispatch) return true

          const rowStart = rowPos + 1
          row.forEach((child, offset) => {
            const childPos = rowStart + offset
            tr.setNodeMarkup(childPos, undefined, { ...child.attrs, widthPercent: evenWidth })
          })

          const appendPos = rowStart + row.content.size
          tr.insert(appendPos, item)
          dispatch(tr.scrollIntoView())
          return true
        },

      removeRowImageAt:
        (rowPos, index) =>
        ({ state, dispatch, tr }) => {
          const row = state.doc.nodeAt(rowPos)
          if (!row || row.type.name !== "newsletterImageRow") return false
          if (index < 0 || index >= row.childCount) return false
          if (row.childCount <= 1) return false

          let offset = rowPos + 1
          for (let i = 0; i < index; i += 1) {
            offset += row.child(i).nodeSize
          }
          const item = row.child(index)

          if (!dispatch) return true

          tr.delete(offset, offset + item.nodeSize)
          // Rebalance remaining siblings to even widths.
          const remainingCount = row.childCount - 1
          const evenWidth = defaultWidthFor(remainingCount)
          const rowStart = rowPos + 1
          let scan = 0
          row.forEach((child, childOffset, i) => {
            if (i === index) return
            const childPos = rowStart + (scan)
            scan += child.nodeSize
            const mapped = tr.mapping.map(rowStart + childOffset, 1)
            const node = tr.doc.nodeAt(mapped)
            if (node) {
              tr.setNodeMarkup(mapped, undefined, { ...node.attrs, widthPercent: evenWidth })
            }
          })
          dispatch(tr)
          return true
        },
    }
  },
})

function buildItem(schema, entry, widthPercent = 50) {
  const attrs = typeof entry === "string" ? { src: entry, alt: "" } : entry
  const media = schema.nodes.newsletterImageMedia.create(attrs)
  const caption = schema.nodes.imageCaption.createAndFill()
  if (!media || !caption) return null
  return schema.nodes.newsletterImageRowItem.createAndFill(
    { widthPercent: normalizeItemWidth(widthPercent) },
    [media, caption]
  )
}

function buildRowView(initialNode, editor, getPos) {
  const dom = document.createElement("div")
  dom.setAttribute("data-newsletter-image-row", "")
  dom.className = "nl-image-row"

  const contentDOM = document.createElement("div")
  contentDOM.className = "nl-image-row__items"

  const addButton = document.createElement("button")
  addButton.type = "button"
  addButton.className = "nl-image-row__add"
  addButton.textContent = "+ image"
  addButton.contentEditable = "false"
  addButton.addEventListener("mousedown", (event) => event.preventDefault())
  addButton.addEventListener("click", (event) => {
    event.preventDefault()
    const pos = typeof getPos === "function" ? getPos() : null
    if (pos === null || pos === undefined) return
    const callback = editor.storage.newsletterImageRow?.onAddImageClick
    if (typeof callback === "function") {
      callback(pos)
    }
  })

  dom.append(contentDOM, addButton)

  const refresh = (node) => {
    dom.dataset.gap = node.attrs.gap || "normal"
    addButton.hidden = node.childCount >= MAX_IMAGE_ROW_ITEMS
  }

  refresh(initialNode)

  return {
    dom,
    contentDOM,
    update(node) {
      if (node.type.name !== initialNode.type.name) return false
      refresh(node)
      return true
    },
    selectNode() {
      dom.classList.add("nl-image-row--selected")
    },
    deselectNode() {
      dom.classList.remove("nl-image-row--selected")
    },
    ignoreMutation(mutation) {
      return !contentDOM.contains(mutation.target)
    },
  }
}
